import { Dictionary } from 'ts-essentials';

import {
  Configuration,
  SortFn,
  TreeItem,
} from './convertFlatItemsToTree.types';

/**
 * Creates a nested tree of items from a flat list of items.
 *
 * @remarks
 *   Modified version of
 *   {@link https://gist.github.com/ggondim/35376795cb832103e466fc158db74af4 this gist}.
 * @example
 *   const input = [
 *     { id: 'a', label: 'A' },
 *     { id: 'b', label: 'B', parent_id: 'a' },
 *   ];
 *
 *   convertFlatItemsToTree(input, {
 *     idKey: 'id',
 *     parentIdKey: 'parent_id',
 *     childrenKey: 'items',
 *   });
 *
 *   const output = [
 *     {
 *       id: 'a',
 *       label: 'A',
 *       items: [{ id: 'b', label: 'B', parent_id: 'a' }],
 *     },
 *   ];
 *
 * @param items The flat items to convert to a tree.
 * @param config The configuration.
 */
export function convertFlatItemsToTree<
  TItem extends Dictionary<unknown>,
  TIdKey extends keyof TItem,
  TParentIdKey extends keyof Omit<TItem, TIdKey>,
  TChildrenKey extends string,
>(
  items: readonly TItem[],
  config: Configuration<TItem, TIdKey, TParentIdKey, TChildrenKey>,
): readonly TreeItem<TItem, TChildrenKey>[] {
  const { idKey, parentIdKey, childrenKey, sortFn } = config;
  const result: TreeItem<TItem, TChildrenKey>[] = [];

  for (let index = 0; index < items.length; index++) {
    if (items[index][parentIdKey]) {
      const parent = items
        .filter(
          (item) =>
            // On consumption we want strong type safety when choosing id and
            // parent id keys, which in turn conflicts with TypeScript here,
            // hence why we cast.
            (item[idKey] as TItem[keyof TItem]) ===
            (items[index][parentIdKey] as TItem[keyof TItem]),
        )
        .pop() as TreeItem<TItem, TChildrenKey>;

      if (!parent[childrenKey]) {
        Object.assign(parent, { [childrenKey]: [] });
      }

      parent[childrenKey].push(items[index] as TreeItem<TItem, TChildrenKey>);
    } else {
      result.push(items[index] as TreeItem<TItem, TChildrenKey>);
    }
  }

  if (sortFn) {
    sortTree(result, childrenKey, sortFn);
  }

  return result;
}

/**
 * Recursively sorts the items.
 *
 * @param items The tree items to sort.
 * @param childrenKey The key that stores the nested items.
 * @param sortFn The sort predicate function.
 */
function sortTree<
  TItem extends Dictionary<unknown>,
  TChildrenKey extends string,
>(
  items: TreeItem<TItem, TChildrenKey>[],
  childrenKey: TChildrenKey,
  sortFn: SortFn<TItem>,
): void {
  items.sort(sortFn);

  items.forEach((item) => {
    if (item[childrenKey]) {
      sortTree(item[childrenKey], childrenKey, sortFn);
    }
  });
}
